import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Link as MuiLink,
  Menu,
  MenuItem,
  Stack,
  Typography
} from '@mui/material';
import {
  ArrowBack,
  DirectionsRun,
  Home,
  List as ListIcon,
  Map as MapIcon,
  MoreVert,
  OpenInNew,
  People,
  Settings as SettingsIcon,
  Tune
} from '@mui/icons-material';
import dayjs from 'dayjs';
import {toast} from 'react-toastify';
import {Link, useNavigate, useParams} from 'react-router-dom';
import EventForm from './EventForm';
import EditButton from 'components/forms/EditButton';
import {useRecordContext} from 'components/record/useRecordContext';
import ShowTabs from 'components/tabs/ShowTabs';
import TabElement from 'components/tabs/TabElement';
import EventSettings from './EventSettings';
import {ResourceContextProvider} from 'components/record/ResourceContext';
import ResourceRecordContextProvider from 'components/record/ResourceRecordProvider';
import SubEvents from 'components/SubEvent/SubEvents';
import Controls from 'components/Control/Controls';
import EventMaps from 'components/Map/EventMaps';
import Entries from 'components/Entry/Entries';
import EventCompetitors from 'components/Competitor/EventCompetitors';
import EventOverview from './EventOverview';
import EventStatusBadge from 'components/admin/EventStatusBadge';
import DeleteButton from 'components/forms/DeleteButton';
import ActionButton from 'components/common/ActionButton';

const TAB_ALIASES: Record<string, string> = {
  '0': 'subevents',
  '1': 'controls',
  '2': 'entries',
  '3': 'competitors',
  tracking: 'overview'
};

const EventPage = () => {
  const navigate = useNavigate();
  const {record: event} = useRecordContext();
  const {tab} = useParams();
  const [menuEl, setMenuEl] = React.useState<null | HTMLElement>(null);

  const activeTab = TAB_ALIASES[tab || ''] || tab || 'overview';
  const publicUrl = `${import.meta.env.VITE_DOMAIN_EVENTS}${event.slug}`;
  const replayUrl = `${import.meta.env.VITE_DOMAIN_REPLAY}${event.slug}`;
  const subCount = event.subEvents?.length || 0;

  const onDeleted = () => {
    navigate('/my_events');
  };

  return (
    <Box>
      <MuiLink
        component={Link}
        to="/my_events"
        sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 2, color: 'text.secondary', fontSize: 14}}
      >
        <ArrowBack fontSize="small" /> Back to events
      </MuiLink>

      <Stack direction={{xs: 'column', md: 'row'}} justifyContent="space-between" spacing={2} sx={{mb: {xs: 2, md: 3}}}>
        <Stack direction="row" spacing={{xs: 1.5, sm: 2}} alignItems="flex-start">
          <Box
            sx={{
              width: {xs: 44, sm: 56},
              height: {xs: 44, sm: 56},
              flexShrink: 0,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <DirectionsRun />
          </Box>
          <Box sx={{minWidth: 0}}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h4" component="h1" sx={{wordBreak: 'break-word'}}>
                {event.name}
              </Typography>
              <EventStatusBadge status={event.status} hidden={event.hidden} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{mt: 0.5, fontSize: {xs: 12, sm: 14}}}>
              {dayjs(event.startDate).format('D MMM YYYY')} | {dayjs(event.startDate).format('HH:mm')} – {dayjs(event.endDate).format('HH:mm')} |{' '}
              {event.category ? event.category.charAt(0).toUpperCase() + event.category.slice(1) : ''} | {subCount}{' '}
              {subCount === 1 ? 'subevent' : 'subevents'}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{width: {xs: '100%', md: 'auto'}}}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNew />}
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{flex: {xs: 1, md: 'none'}}}
          >
            Open public page
          </Button>
          <EditButton label="Edit event" variant="outlined" size="small" sx={{flex: {xs: 1, md: 'none'}}}>
            <EventForm />
          </EditButton>
          <ActionButton
            method="syncActivityCompetitors"
            label="Check for competitors"
            size="small"
            variant="outlined"
            sx={{flex: {xs: 1, md: 'none'}}}
            variables={{id: event.slug}}
            confirmTitle="Check for competitors"
            confirmMessage="Create competitors on the first public subevent from Garmin, Suunto, and Coros activities that overlap this event. The event setting and each user's preference must both be on. Existing competitors are left unchanged."
            onSuccess={(data) => {
              const result = (data as {item?: {created?: number; skipped?: number}} | undefined)?.item;
              const created = result?.created ?? 0;
              const skipped = result?.skipped ?? 0;
              if (created === 0 && skipped === 0) {
                toast.info('No new competitors found.');
                return;
              }
              toast.success(`Created ${created} ${created === 1 ? 'competitor' : 'competitors'}. Skipped ${skipped} already on the event.`);
            }}
          />
          <IconButton onClick={(e) => setMenuEl(e.currentTarget)}>
            <MoreVert />
          </IconButton>
          <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
            <MenuItem component="a" href={publicUrl} target="_blank" rel="noopener noreferrer" onClick={() => setMenuEl(null)}>
              Open Live Tracking
            </MenuItem>
            <MenuItem component="a" href={replayUrl} target="_blank" rel="noopener noreferrer" onClick={() => setMenuEl(null)}>
              Open Replay
            </MenuItem>
            <MenuItem onClick={() => { setMenuEl(null); navigate(`/my_events/${event.slug}/settings`); }}>
              Settings
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      <ShowTabs baseUrl={`/my_events/${event.slug}`} value={activeTab} defaultValue="overview">
        <TabElement label="Overview" value="overview" icon={<Home />}>
          <EventOverview />
        </TabElement>
        <TabElement label="Subevents" value="subevents" icon={<ListIcon />}>
          <SubEvents />
        </TabElement>
        <TabElement label="Maps" value="maps" icon={<MapIcon />}>
          <EventMaps />
        </TabElement>
        <TabElement label="Entries" value="entries" icon={<People />}>
          <ResourceContextProvider resource="Entry" params={{slug: event.slug}}>
            <Entries filters={{slug: event.slug}} />
          </ResourceContextProvider>
        </TabElement>
        <TabElement label="Competitors" value="competitors" icon={<DirectionsRun />}>
          <EventCompetitors />
        </TabElement>
        <TabElement label="Controls" value="controls" icon={<Tune />}>
          <Controls />
        </TabElement>
        <TabElement label="Settings" value="settings" icon={<SettingsIcon />}>
          <ResourceContextProvider resource="EventSettings">
            <ResourceRecordContextProvider id={event.slug}>
              <EventSettings />
            </ResourceRecordContextProvider>
          </ResourceContextProvider>
          <Box sx={{mt: 3, p: {xs: 1.5, sm: 2.5}, border: '1px solid', borderColor: 'error.light', borderRadius: 2}}>
            <Typography variant="subtitle1" color="error" fontWeight={700} sx={{mb: 1}}>
              Danger zone
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
              Destructive actions are kept here, away from everyday event operations.
            </Typography>
            <DeleteButton onSuccess={onDeleted} variant="outlined" color="error">
              Are you sure you want to delete this event?
            </DeleteButton>
          </Box>
        </TabElement>
      </ShowTabs>
    </Box>
  );
};

export default EventPage;
